import React, { useState, useEffect, useMemo } from "react";
import { Redirect, useParams, useHistory, Prompt } from "react-router-dom";
import client, { signUpload } from "@culturehq/client";
import { Info, Warning } from "@culturehq/components";

import { useClickTransport, useProfile, useInterests, useLocations, useValues } from "app/context";
import { t, Trans } from "app/utils";
import Event from "components/events/Event";
import { CreateEventSidebar } from "components/common/EventSidebar";
import { noSlackBroadcastValue } from "components/event/edit/SlackChannelField";
import tagsTypes from "constants/tagsTypes";
import { getNextFormPath } from "./eventForms";
import EventType from "./eventForms/EventType";
import EventBasicInfo from "./eventForms/EventBasicInfo";
import Visibility from "./eventForms/Visibility";
import AdvancedSettings from "./eventForms/AdvancedSettings";
import EventFooter from "./EventFooter";
import EventSuccess from "./EventSuccess";

import "./styles.scss";

const filterParams = values => {
  const filtered = {};
  const allowed = client.createEvent.optionalParams.concat(client.updateEvent.optionalParams);

  Object.keys(values).forEach(key => {
    if (allowed.includes(key)) {
      filtered[key] = values[key];
    }
  });

  return filtered;
};

const makeSelectOptions = (entities, labelKey, valueKey, valueCategory) => {
  const selectOptions = [];

  Object.keys(entities).forEach(entityId => {
    const { [labelKey]: label, [valueKey]: value } = entities[entityId];
    let option = { label, value };
    if (valueCategory) {
      option = { ...option, category: valueCategory };
    }
    selectOptions.push(option);
  });

  return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
};

const useEventSelectOptions = () => {
  const { interests, onAllInterestsRequested } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllOrganizationValuesRequested();
  }, [onAllInterestsRequested, onAllLocationsRequested, onAllOrganizationValuesRequested]);

  return {
    interestOptions: useMemo(
      () => makeSelectOptions(interests, "name", "name"),
      [interests]
    ),
    locationOptions: useMemo(
      () => makeSelectOptions(locations, "name", "id"),
      [locations]
    ),
    organizationValueOptions: useMemo(
      () => makeSelectOptions(organizationValues, "name", "id", tagsTypes.ORG_VALUES),
      [organizationValues]
    ),
    onAllLocationsRequested,
    onAllOrganizationValuesRequested
  };
};

const EventCreateContext = props => {
  const { eventsId, eventForm } = useParams();
  const history = useHistory();
  const { event: propsEvent,
    profile: { organizationAdmin },
    clickedUsers: inviteUsers,
    clickedValues: selectedOrganizationValues,
    onEventUpdate = () => {} } = props;
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgValues, setOrgValues] = useState([]);

  const [eventState, setEventState] = useState(propsEvent || {
    openInvites: true,
    inviteOnly: inviteUsers.length > 0,
    allLocations: true,
    createSurvey: organizationAdmin,
    imageDefault: true,
    sponsored: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hostOnlySurvey: false,
    public: true,
    timezoneOffset: -new Date().getTimezoneOffset(),
    image: null,
    imageBlob: null
  });
  const [redirectTo, setRedirectTo] = useState("");
  const [unsaved, setUnsaved] = useState(false);

  React.useEffect(() => {
    setUnsaved(false);
  }, [eventForm, setUnsaved]);

  useEffect(
    () => {
      setOrgValues(selectedOrganizationValues);
    },
    [selectedOrganizationValues]
  );

  const {
    interestOptions,
    locationOptions,
    organizationValueOptions
  } = useEventSelectOptions();

  const handleUnSaved = val => {
    setUnsaved(val);
  };

  const handleEventState = val => {
    setEventState({ ...eventState, ...val });

    if (eventForm !== "advanced") {
      history.push(getNextFormPath(eventForm, eventsId));
    } else if (!eventState.id) {
      setLoading(true);
    } else {
      history.push(`/events/${eventState.id}`);
    }
  };

  const handleImage = (image, imageBlob) => {
    setEventState({ ...eventState, image, imageBlob });
  };

  const onAWSUploadedFile = async (eventId, queryString) => {
    setRedirectTo(`/events/${eventId}${queryString}`);
    setSuccess(true);
  };

  const waitForS3 = async (ms, eventId, queryString) => (
    new Promise(_ => {
      // This timeout is to allow AWS to upload the file, in this point,
      // AWs has responded with a 200 status, but we need to take care of
      // S3 Consistency Model
      setTimeout(onAWSUploadedFile, ms, eventId, queryString);
    })
  );

  const handleSubmit = async ({ image, locationIds, ...values }) => {
    const { surveyId, createdAt, updatedAt, allLocations, ...rest } = values;
    const { id: eventId } = values;
    let imageUrl;

    if (eventState.imageBlob && eventState.imageBlob.size) {
      imageUrl = await signUpload(eventState.imageBlob);
    }

    const processed = {
      ...filterParams(rest)
    };

    if (!allLocations) {
      processed.allLocations = false;
    }

    if (locationIds) {
      processed.locationIds = locationIds;
    }

    if (orgValues.length > 0) {
      processed.organizationValueIds = orgValues.map(value => value.value);
    }

    if (imageUrl) {
      processed.imageDefault = false;
      processed.imageUrl = imageUrl;
    }

    if (values.slackChannel && values.slackChannel !== noSlackBroadcastValue) {
      processed.slackChannel = values.slackChannel;
    }

    if (!propsEvent) {
      processed.inviteeIds = inviteUsers.map(({ id }) => id);
    }

    try {
      let queryString = "";
      let event;

      if (eventId) {
        ({ event } = await client.updateEvent({ ...processed, eventId }));
      } else {
        ({ event } = await client.createEvent(processed));
        queryString = `#${processed.inviteeIds.length ? "attendees" : "invite"}`;
      }

      onEventUpdate(event);

      // This timeout is to allow AWS to upload the file, in this point,
      // AWs has responded with a 200 status, but we need to take care of
      // S3 Consistency Model
      await waitForS3(2000, event.id, queryString);
    } catch (error) {
      if (error.status === 422) {
        setSuccess(false);
        setLoading(false);
      } else {
        setSuccess(false);
        setLoading(false);
        throw error;
      }
    }
  };

  const onSelectedOrgValues = values => {
    setOrgValues(values);
    handleUnSaved(true);
  };

  if (success) {
    return <Redirect to={redirectTo} />;
  }

  if (redirectTo && !success && !loading) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <>
      <Prompt when={!eventState.id && unsaved} message={t("You have unsaved changes, are you sure you want to leave?")} />
      {eventForm === "type" && (
        <EventType
          eventState={eventState}
          handleEventState={handleEventState}
          handleImage={handleImage}
          organizationValueOptions={organizationValueOptions}
          selectedOrganizationValues={orgValues}
          onSubmit={handleSubmit}
          handleUnSaved={handleUnSaved}
          onSelectedOrgValues={onSelectedOrgValues}
        />
      )}
      {eventForm === "basic" && (
        <EventBasicInfo
          eventState={eventState}
          handleEventState={handleEventState}
          handleImage={handleImage}
          onSubmit={handleSubmit}
          handleUnSaved={handleUnSaved}
        />
      )}
      {eventForm === "visibility" && (
        <Visibility
          eventState={eventState}
          handleEventState={handleEventState}
          interestOptions={interestOptions}
          locationOptions={locationOptions}
          onSubmit={handleSubmit}
          handleUnSaved={handleUnSaved}
        />
      )}
      {eventForm === "advanced" && (
        <AdvancedSettings
          eventState={eventState}
          handleEventState={handleEventState}
          onSubmit={handleSubmit}
          handleUnSaved={handleUnSaved}
        />
      )}
      {loading && <EventSuccess />}
    </>
  );
};

const EventUpdate = props => {
  const { event: eventData } = props;
  const event = new Event(eventData);
  const clickTransportState = useClickTransport();
  const {
    clickedPagination: pagination,
    clickedOptions: options,
    clickedUsers: users
  } = clickTransportState;

  const { profile } = useProfile();
  const [footerLoaded, setFooterLoaded] = useState(false);
  const { onEventUpdate = () => {} } = props;

  return (
    <div className={onEventUpdate ? "Event__Update" : ""}>
      {event?.cancelledAt && (
        <Warning>
          <Trans>This event has been cancelled.</Trans>
        </Warning>
      )}

      {event?.isPast() && !event?.cancelledAt && (
        <Info>
          <Trans>This event has ended.</Trans>
        </Info>
      )}
      {!eventData
        ? (
          <div className="event_create Events clearfix">
            <CreateEventSidebar />
            <div className="work_area">
              {footerLoaded && (
                <EventCreateContext
                  {...props}
                  {...clickTransportState}
                  profile={profile}
                  footerLoaded={footerLoaded}
                />
              )}
            </div>
            <EventFooter
              setFooterLoaded={setFooterLoaded}
              pagination={pagination}
              options={options}
              users={users}
            />
          </div>
        )
        : (
          <>
            {footerLoaded && (
              <EventCreateContext
                {...props}
                {...clickTransportState}
                profile={profile}
                footerLoaded={footerLoaded}
              />
            )}
            <EventFooter
              setFooterLoaded={setFooterLoaded}
              pagination={pagination}
              options={options}
              users={users}
            />
          </>
        )}
    </div>
  );
};

export default EventUpdate;
